import { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaFileContract } from 'react-icons/fa';
import { Button, Pagination } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import { UserContext } from '../../context/UserContext';
import { permissions } from '../../utils/permissions-utils';
import { getDepartmentHomepage } from '../../utils/navigation-utils';
import API from '../../config/axiosConfig';
import CommonLayout from '../../components/CommonLayout';
import ProtocolSearchBox from './ProtocolSearchBox';
import useRecentProtocolsManager from './RecentProtocolsManager';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import EntitiesList from './EntitiesList';
import FloatingAddButton from '../../components/FloatingAddButton';
import EmekidProtocolsIndex from './EmekidProtocolsIndex';
import './Protocols.scss';
import ProtocolsAiChat from './ProtocolsAiChat';

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const theme = createTheme({ typography: { fontFamily: '"Rubik", sans-serif' },
  direction: 'rtl',
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#1FB5A3',
          color: 'white',
          margin: '0 4px 4px 0',
        },
      },
    },
  },
});

function Protocols() {
  const [protocols, setProtocols] = useState([]);
  const [filteredProtocols, setFilteredProtocols] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteProtocol, setDeleteProtocol] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAnyFilterActive, setIsAnyFilterActive] = useState(false);

  const [categoryTags, setCategoryTags] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, outdatedProtocols, hospitals } = useContext(UserContext);
  const { getLastRecentlyUsedProtocols } = useRecentProtocolsManager();

  // Check if user's department homepage is /Emekid or if there's an emekid query parameter
  const hasEmekidParam = new URLSearchParams(location.search).get('emekid') === 'true';
  const isEmekidHomepage = getDepartmentHomepage(user, hospitals)?.toLowerCase() === '/emekid' || hasEmekidParam;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasSearchFilters = searchParams.get('hospital') ||
                             searchParams.get('dep') ||
                             searchParams.get('tags') ||
                             searchParams.get('term') ||
                             searchParams.get('outdated');

    // Only fetch protocols if we're not on Emekid index (has search filters) or we're on regular protocols page
    const shouldFetchProtocols = hasSearchFilters || !isEmekidHomepage;

    if (!shouldFetchProtocols) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const fetchProtocols = async () => {
      setIsLoading(true);
      try {
        const searchParams = new URLSearchParams(location.search);
        const hospitalParam = searchParams.get('hospital');
        const depParam = searchParams.get('dep');
        const tagsParam = searchParams.get('tags');
        const categoryParam = searchParams.get('category');
        const termParam = searchParams.get('term');
        const outdatedParam = searchParams.get('outdated');
        const pageParam = searchParams.get('page') || '1';
        setCurrentPage(parseInt(pageParam, 10));

        if (outdatedParam === 'true') {
          const res = await API.get('/api/protocols/outdated');
          // The outdated endpoint returns a list, not paginated structure
          const fetchedProtocols = res.data;
          setProtocols(fetchedProtocols);
          setFilteredProtocols(fetchedProtocols);
          setTotalPages(1); // Assuming no pagination for outdated list for now
          setIsAnyFilterActive(true);
          setIsLoading(false);
          return;
        }

        const isCategoryMode = !!categoryParam;

        let parsedCategoryTags = [];
        if (categoryParam) {
          parsedCategoryTags = categoryParam.split('/').map(tag => tag.trim()).filter(Boolean);
        }
        setCategoryTags(parsedCategoryTags);

        const params = new URLSearchParams();
        if (hospitalParam) params.set('hospital', hospitalParam);
        if (depParam) params.set('department', depParam);
        if (termParam) params.set('term', termParam);
        params.set('page', pageParam);

        const allTags = [];
        if (tagsParam) {
          allTags.push(...tagsParam.split(','));
        }
        if (isCategoryMode) {
          allTags.push(...parsedCategoryTags);
        }

        if (allTags.length > 0) {
          params.set('tags', allTags.join(','));
        }

        const res = await API.get(`/api/protocols/search?${params.toString()}`);
        const { protocols: fetchedProtocols, pages } = res.data;

        setIsAnyFilterActive(!!hospitalParam || !!depParam || !!tagsParam || !!termParam);

        const recentlyUsedProtocols = getLastRecentlyUsedProtocols();
        const sorted = fetchedProtocols.sort((a, b) => {
          const aIndex = recentlyUsedProtocols.findIndex(p => p.id === a._id);
          const bIndex = recentlyUsedProtocols.findIndex(p => p.id === b._id);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });

        setProtocols(sorted);
        setFilteredProtocols(sorted);
        setTotalPages(pages);

      } catch (error) {
        console.error('Error fetching protocols:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProtocols();
  }, [location.search, isEmekidHomepage]);

  const handleSearch = (filters) => {
    const searchParams = new URLSearchParams(location.search);

    if (filters.hospital) {
      searchParams.set('hospital', filters.hospital);
    } else {
      searchParams.delete('hospital');
    }

    if (filters.department) {
      searchParams.set('dep', filters.department);
    } else {
      searchParams.delete('dep');
    }

    if (filters.tags && filters.tags.length > 0) {
      searchParams.set('tags', filters.tags.join(','));
    } else {
      searchParams.delete('tags');
    }

    if (filters.searchTerm) {
      searchParams.set('term', filters.searchTerm);
    } else {
      searchParams.delete('term');
    }

    if (filters.outdated) {
      searchParams.set('outdated', 'true');
    } else {
      searchParams.delete('outdated');
    }

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  };


  const handleProtocolSelect = (protocolId) => {
    navigate(`/protocols/${protocolId}`);
  };

  const handleView = (id) => {
    handleProtocolSelect(id);
  };

  const handleEdit = (e, id) => {
    e.stopPropagation();
    navigate(`/protocols/${id}/edit`);
  };

  const openDeleteConfirm = (e, protocol) => {
    e.stopPropagation();
    setDeleteProtocol(protocol);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSuccess = () => {
    // Update local state after successful deletion
    setProtocols(protocols.filter(p => p._id !== deleteProtocol._id));
    setFilteredProtocols(filteredProtocols.filter(p => p._id !== deleteProtocol._id));
    setDeleteProtocol(null);
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/api/protocols/${deleteProtocol._id}`);
      setDeleteConfirmOpen(false);
      handleDeleteSuccess();
    } catch (error) {
      console.error('Deletion failed:', error);
      const message = error.response?.data?.message || 'שגיאה במחיקת הפרוטוקול. אנא נסה שנית.';
      setErrorMessage(message);
      setShowError(true);
    }
  };

  const handleAddNewProtocol = () => {
    const searchParams = new URLSearchParams(location.search);
    const depParam = searchParams.get('dep');

    if (depParam) {
      navigate(`/protocols/new?dep=${depParam}`);
    } else {
      navigate('/protocols/new');
    }
  };

  const canCreateProtocol = () => {
    return permissions.canCreate(user);
  };

  const canEditProtocol = (protocol) => {
    if (!protocol || !protocol.departments || protocol.departments.length === 0) return false;
    // Assuming a protocol is associated with one department in this context
    const protocolWithDepartment = { ...protocol, department: protocol.departments[0] };
    return permissions.canEdit(user, protocolWithDepartment);
  };

  const handlePageChange = (event, value) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', value);
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  };

  // Determine if we should show the Emekid index or protocol list
  const searchParams = new URLSearchParams(location.search);
  const hasSearchFilters = searchParams.get('hospital') ||
                           searchParams.get('dep') ||
                           searchParams.get('tags') ||
                           searchParams.get('term') ||
                           searchParams.get('outdated');
  // dept is for Emekid internal navigation, not a search filter
  const showEmekidIndex = isEmekidHomepage && !hasSearchFilters;

  // Whether the user's scope has at least one protocol to ask the AI chat about.
  // - Still loading, or a filter/search is active: we can't tell from this fetch
  //   alone whether the scope is truly empty, so assume protocols may exist.
  // - Emekid's static category menu never fetches protocols itself: same reasoning.
  // - Only when an unfiltered fetch has completed and returned zero protocols do we
  //   know for certain the scope is empty (the same condition the empty-state below uses).
  const hasProtocols = showEmekidIndex || isLoading || isAnyFilterActive || filteredProtocols.length > 0;

  return (
    <CommonLayout>
      <div className="protocols-container" style={{ position: 'relative', minHeight: '50dvh' }}>
        <CacheProvider value={cacheRtl}>
          <ThemeProvider theme={theme}>
            {outdatedProtocols && outdatedProtocols.length > 0 && (
              <div
                style={{
                  backgroundColor: '#FFF4E1',
                  padding: '10px',
                  marginBottom: '10px',
                  borderRadius: '4px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
                onClick={() => {
                  navigate({
                    pathname: location.pathname,
                    search: '?outdated=true'
                  });
                }}
              >
                ישנם {outdatedProtocols.length} פרוטוקולים שדורשים תיקוף מחדש
              </div>
            )}

            <ProtocolSearchBox
              onSearch={handleSearch}
              onProtocolSelect={handleProtocolSelect}
              categoryTags={categoryTags}
            />

            {showEmekidIndex ? (
              <EmekidProtocolsIndex />
            ) : (
              <div className="protocols-list">
                {!!isLoading && [1, 2, 3].map(item => (<SkeletonCard key={item} />))}
                {!isLoading && filteredProtocols.length > 0 && <EntitiesList
                  items={filteredProtocols}
                  toPrefix="protocols"
                  canEdit={canEditProtocol}
                  onEdit={handleEdit}
                  onDelete={openDeleteConfirm}
                />}
                {!isLoading && filteredProtocols.length === 0 && isAnyFilterActive && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    minHeight: '50dvh'
                  }}>
                    <p style={{
                      fontSize: '1.5rem',
                      direction: 'rtl',
                      color: '#666'
                    }}>
                      לא נמצאו פרוטוקולים
                    </p>
                  </div>
                )}
                {!isLoading && filteredProtocols.length === 0 && !isAnyFilterActive && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    minHeight: '50dvh'
                  }}>
                    <FaFileContract size={60} style={{ marginBottom: '20px', color: '#0C3058' }} />
                    <p style={{
                      fontSize: '1.5rem',
                      maxWidth: '600px',
                      marginBottom: '30px',
                      direction: 'rtl'
                    }}>
                      {permissions.isWaitingForApproval(user) ?
                        "ברגע שהצטרפותך למחלקה תאושר, יהיה באפשרותך להעלות את פרוטוקולי הטיפול של המחלקה שלך, לגשת אליהם מהמחשב, הטאבלט או הסמארטפון ולשתף אותם עם שאר חברי המחלקה." :
                        "כאן באפשרותך להעלות את פרוטוקולי הטיפול של המחלקה שלך, לגשת אליהם מהמחשב, הטאבלט או הסמארטפון ולשתף אותם עם שאר חברי המחלקה."
                      }
                    </p>
                    {canCreateProtocol() && <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center', width: '100%', maxWidth: '300px' }}>
                      <button
                        onClick={() => navigate('/protocols/new')}
                        style={{
                          backgroundColor: 'white',
                          color: '#0C3058',
                          border: '1px solid #0C3058',
                          padding: '10px 20px',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                          fontWeight: 'bold',
                          width: '100%'
                        }}>
                        העלאת פרוטוקול חדש
                      </button>
                    </div>}
                  </div>
                )}
              </div>
            )}

            {!showEmekidIndex && totalPages > 1 && (
              <div className="pagination-container">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </div>
            )}

            <DeleteConfirmationDialog
              open={deleteConfirmOpen}
              onClose={() => setDeleteConfirmOpen(false)}
              onConfirm={handleDelete}
              title={`האם אתה בטוח שברצונך למחוק את הפרוטוקול ${deleteProtocol?.name}?`}
              showError={showError}
              errorMessage={errorMessage}
              onCloseError={() => setShowError(false)}
            />
          </ThemeProvider>
        </CacheProvider>
        <ProtocolsAiChat isAddButtonVisible={canCreateProtocol()} hasProtocols={hasProtocols} />
        {canCreateProtocol() && (
          <FloatingAddButton
            onClick={handleAddNewProtocol}
            entityName="פרוטוקול"
            dataLoaded={!isLoading}
          />
        )}
      </div>
    </CommonLayout>
  );
}

const SkeletonCard = () => {
  return (
    <div className="skeleton-protocol">
      <div className="skeleton-right">
        <div className="skeleton-title" />
      </div>
      <div className="skeleton-left">
        <div className="skeleton-icon" />
        <div className="skeleton-icon" />
      </div>
    </div>
  );
};


export default Protocols;
